import { useState, useEffect, useCallback, useRef } from 'react';

export function useSpeechSynthesis({ onSpeechEnd } = {}) {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    try {
      return localStorage.getItem('ai_tts_muted') === 'true';
    } catch {
      return false;
    }
  });

  const onSpeechEndRef = useRef(onSpeechEnd);
  useEffect(() => {
    onSpeechEndRef.current = onSpeechEnd;
  }, [onSpeechEnd]);

  const synth = typeof window !== 'undefined' ? window.speechSynthesis : null;
  const hasSupport = Boolean(synth);
  const utteranceRef = useRef(null);

  // Clean text and insert natural human breathing pauses and punctuation
  const cleanTextForSpeech = (rawText) => {
    if (!rawText) return '';

    let text = rawText;

    // 1. Remove markdown code blocks and HTML
    text = text.replace(/```[\s\S]*?```/g, '');
    text = text.replace(/`([^`]+)`/g, '$1');
    text = text.replace(/<[^>]*>/g, '');
    text = text.replace(/https?:\/\/\S+/g, '');

    // 2. Format markdown headings to full declarative sentences with pause
    text = text.replace(/^#{1,6}\s*(.+)$/gm, '$1. ');

    // 3. Convert table rows into readable spoken pauses instead of raw pipes
    text = text.replace(/\|[\s-:]+\|/g, ''); // Table divider lines
    text = text.replace(/\|/g, ', ');

    // 4. Convert bullet points and numbered lists into natural spoken pauses
    text = text.replace(/^[\s]*[•*-]\s+/gm, ', ');
    text = text.replace(/^[\s]*1\.\s+/gm, 'First, ');
    text = text.replace(/^[\s]*2\.\s+/gm, 'Second, ');
    text = text.replace(/^[\s]*3\.\s+/gm, 'Third, ');
    text = text.replace(/^[\s]*4\.\s+/gm, 'Fourth, ');
    text = text.replace(/^[\s]*5\.\s+/gm, 'Fifth, ');
    text = text.replace(/^[\s]*6\.\s+/gm, 'Sixth, ');
    text = text.replace(/^[\s]*[0-9]+\.\s+/gm, ', ');

    // 5. Convert bold/italic emphasis to natural words
    text = text.replace(/\*\*([^*]+)\*\*/g, '$1');
    text = text.replace(/\*([^*]+)\*/g, '$1');
    text = text.replace(/_([^_]+)_/g, '$1');

    // 6. Expand currencies with natural breathing pauses
    text = text
      .replace(/₹\s*([0-9,.]+)\s*Cr\b/gi, '$1 Crore Rupees, ')
      .replace(/₹\s*([0-9,.]+)\s*L\b/gi, '$1 Lakh Rupees, ')
      .replace(/₹\s*([0-9,.]+)/g, '$1 Rupees, ')
      .replace(/\$\s*([0-9,.]+)/g, '$1 Dollars, ')
      .replace(/£\s*([0-9,.]+)/g, '$1 Pounds, ')
      .replace(/%\b/g, ' per cent, ');

    // 7. Expand enterprise acronyms with clear phonetic diction
    text = text
      .replace(/\bPO\b/g, 'Purchase Order')
      .replace(/\bPOs\b/g, 'Purchase Orders')
      .replace(/\bPR\b/g, 'Purchase Requisition')
      .replace(/\bPRs\b/g, 'Purchase Requisitions')
      .replace(/\bGRN\b/g, 'Goods Receipt Note')
      .replace(/\bGRNs\b/g, 'Goods Receipt Notes')
      .replace(/\bAP\b/g, 'Accounts Payable')
      .replace(/\bAR\b/g, 'Accounts Receivable')
      .replace(/\bGL\b/g, 'General Ledger')
      .replace(/\bSKU\b/g, 'S-K-U')
      .replace(/\bSKUs\b/g, 'S-K-Us')
      .replace(/\bGRC\b/g, 'Governance, Risk and Compliance')
      .replace(/\bSLA\b/g, 'Service Level Agreement')
      .replace(/\bSLAs\b/g, 'Service Level Agreements')
      .replace(/\bRFQ\b/g, 'Request for Quotation')
      .replace(/\bRFQs\b/g, 'Requests for Quotations')
      .replace(/\bRBAC\b/g, 'R-B-A-C')
      .replace(/\bGSTIN\b/g, 'G-S-T-I-N')
      .replace(/\bGST\b/g, 'G-S-T')
      .replace(/\bTDS\b/g, 'T-D-S')
      .replace(/\bPAN\b/g, 'P-A-N')
      .replace(/\bVAT\b/g, 'V-A-T')
      .replace(/\bUTR\b/g, 'U-T-R')
      .replace(/\bPDF\b/g, 'P-D-F')
      .replace(/\bCSV\b/g, 'C-S-V')
      .replace(/\bCXO\b/g, 'C-X-O')
      .replace(/\bQC\b/g, 'Quality Check')
      .replace(/\bUI\b/g, 'User Interface');

    // 8. Remove symbols, brackets, and emojis
    text = text.replace(/[[\]()~]/g, '');
    text = text.replace(/[\u{1F300}-\u{1F9FF}\u{2600}-\u{26FF}\u{2700}-\u{27BF}]/gu, '');

    // 9. Convert colons after titles to clean comma pauses
    text = text.replace(/:\s+/g, ', ');

    // 10. Handle line breaks: turn double line breaks into periods with pauses
    text = text.replace(/\n\s*\n+/g, '. ');
    text = text.replace(/\n+/g, ', ');

    // 11. Normalize punctuation spacing so voice breathes naturally
    text = text
      .replace(/,\s*,+/g, ', ')
      .replace(/\.\s*\.+/g, '. ')
      .replace(/,\s*\./g, '. ')
      .replace(/\.\s*,/g, '. ')
      .replace(/^[.,\s]+/, '') // Strip stray leading commas or periods
      .replace(/\s+/g, ' ')
      .trim();

    // 12. Smart voice trimming: If response is very long, speak executive summary
    // and conclude naturally to avoid voice fatigue
    const sentences = text.match(/[^.!?]+[.!?]+/g) || [text];
    if (sentences.length > 5) {
      const summary = sentences.slice(0, 4).join(' ').trim();
      return `${summary} I have displayed the full details on your screen.`;
    }

    return text;
  };

  const cancel = useCallback(() => {
    if (synth) {
      try {
        synth.cancel();
      } catch (e) {}
    }
    setIsSpeaking(false);
    setIsPaused(false);
  }, [synth]);

  const pause = useCallback(() => {
    if (synth) {
      try {
        synth.pause();
      } catch (e) {}
      setIsPaused(true);
    }
  }, [synth]);

  const resume = useCallback(() => {
    if (synth) {
      try {
        synth.resume();
      } catch (e) {}
      setIsPaused(false);
    }
  }, [synth]);

  const togglePause = useCallback(() => {
    if (isPaused) {
      resume();
    } else if (isSpeaking) {
      pause();
    }
  }, [isPaused, isSpeaking, pause, resume]);

  const toggleMute = useCallback(() => {
    setIsMuted((prev) => {
      const next = !prev;
      try {
        localStorage.setItem('ai_tts_muted', String(next));
      } catch {}
      if (next) {
        cancel();
      }
      return next;
    });
  }, [cancel]);

  // Find natural human voice (prioritizing Enhanced/Premium UK Female voices)
  const getUKFemaleVoice = useCallback(() => {
    if (!synth) return null;
    const voices = synth.getVoices();
    if (!voices || voices.length === 0) return null;

    const preferredNames = [
      'serena', 'stephanie', 'martha', 'victoria', 'libby', 'sonia',
      'google uk english female', 'kate', 'fiona', 'moira', 'alice',
      'hazel', 'susan', 'emma', 'amy', 'olivia', 'samantha', 'karen'
    ];

    // 1. Top Tier: Enhanced or Premium UK English female voice on macOS
    const enhancedUK = voices.find(v => {
      const isUK = v.lang && (v.lang.toLowerCase().startsWith('en-gb') || v.lang.toLowerCase() === 'en_gb');
      if (!isUK) return false;
      const name = v.name.toLowerCase();
      const isEnhanced = name.includes('enhanced') || name.includes('premium');
      return isEnhanced && preferredNames.some(k => name.includes(k));
    });
    if (enhancedUK) return enhancedUK;

    // 2. High Tier: Named UK English female voice
    const ukFemale = voices.find(v => {
      const isUK = v.lang && (v.lang.toLowerCase().startsWith('en-gb') || v.lang.toLowerCase() === 'en_gb');
      if (!isUK) return false;
      const name = v.name.toLowerCase();
      return preferredNames.some(k => name.includes(k));
    });
    if (ukFemale) return ukFemale;

    // 3. Any UK English voice that is not explicitly male
    const ukVoice = voices.find(v => {
      const isUK = v.lang && (v.lang.toLowerCase().startsWith('en-gb') || v.lang.toLowerCase() === 'en_gb');
      if (!isUK) return false;
      const name = v.name.toLowerCase();
      return !name.includes('male') && !name.includes('george') && !name.includes('daniel') && !name.includes('oliver');
    });
    if (ukVoice) return ukVoice;

    // 4. Any Enhanced English female voice
    const enhancedEnglish = voices.find(v => {
      if (!v.lang || !v.lang.toLowerCase().startsWith('en')) return false;
      const name = v.name.toLowerCase();
      return (name.includes('enhanced') || name.includes('premium')) && preferredNames.some(k => name.includes(k));
    });
    if (enhancedEnglish) return enhancedEnglish;

    // 5. Natural Commonwealth / English female fallback
    const naturalFemale = voices.find(v => {
      if (!v.lang || !v.lang.toLowerCase().startsWith('en')) return false;
      const name = v.name.toLowerCase();
      return preferredNames.some(k => name.includes(k));
    });
    if (naturalFemale) return naturalFemale;

    return voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en-gb')) || 
           voices.find(v => v.lang && v.lang.toLowerCase().startsWith('en')) || null;
  }, [synth]);

  const speak = useCallback((text) => {
    if (!hasSupport || isMuted || !text) return;

    cancel();

    const clean = cleanTextForSpeech(text);
    if (!clean) return;

    const utterance = new SpeechSynthesisUtterance(clean);
    
    // Warm, articulate, composed human cadence
    utterance.rate = 0.94;
    utterance.pitch = 1.0; // Natural executive pitch (not squeaky 1.10)
    utterance.lang = 'en-GB';

    const selectedVoice = getUKFemaleVoice();
    if (selectedVoice) {
      utterance.voice = selectedVoice;
      if (selectedVoice.lang) utterance.lang = selectedVoice.lang;
    }

    utterance.onstart = () => {
      setIsSpeaking(true);
      setIsPaused(false);
    };
    utterance.onpause = () => setIsPaused(true);
    utterance.onresume = () => setIsPaused(false);
    utterance.onend = () => {
      setIsSpeaking(false);
      setIsPaused(false);
      if (onSpeechEndRef.current) {
        onSpeechEndRef.current();
      }
    };
    utterance.onerror = (e) => {
      console.warn('Speech synthesis error:', e);
      setIsSpeaking(false);
      setIsPaused(false);
    };

    utteranceRef.current = utterance;
    try {
      synth.speak(utterance);
    } catch (e) {
      console.warn('TTS speak execution error:', e);
      setIsSpeaking(false);
      setIsPaused(false);
    }
  }, [hasSupport, isMuted, synth, cancel, getUKFemaleVoice]);

  useEffect(() => {
    if (synth && synth.onvoiceschanged !== undefined) {
      synth.onvoiceschanged = () => {
        // Voices loaded
      };
    }
    return () => {
      if (synth) {
        try {
          synth.cancel();
        } catch (e) {}
      }
    };
  }, [synth]);

  return {
    speak,
    cancel,
    stop: cancel,
    pause,
    resume,
    togglePause,
    isSpeaking,
    isPaused,
    isMuted,
    toggleMute,
    hasSupport
  };
}
